import React, { useCallback, useEffect } from 'react';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Checkbox } from '@/shared/components/Checkbox/Checkbox';
import CreateLayout from '@/shared/layout/createLayout';
import {
  CERTIFICATE_LABELS,
  CERTIFICATE_PREVIEW_LABELS,
} from '../../constants/labels';
import { useCreateCertTemplate } from '../../hooks/useCreateCertTemplate';
import { CertificatePreview } from '../viewCertificateTemplate/CertificatePreview';
import type { CertificateTemplateDetail } from '../../types';

// ─── Section divider ──────────────────────────────────────────────────────────

const SectionDivider = () => <hr className="border-[#DCDDE0]" />;

// ─── Field row shimmer ────────────────────────────────────────────────────────

const FieldRowShimmer = () => (
  <div className="border border-[#DCDDE0] rounded-lg px-4 py-2.5 flex items-center gap-3 animate-pulse">
    <div className="w-4.5 h-4.5 rounded-sm bg-[#ECEEEF] shrink-0" />
    <div className="h-4 bg-[#ECEEEF] rounded w-2/5" />
  </div>
);

// ─── Page headers ─────────────────────────────────────────────────────────────

const FormPageHeader: React.FC = () => (
  <p className="text-titleLarge text-neutral-10">
    {CERTIFICATE_LABELS.CREATE_TITLE}
  </p>
);

const PreviewPageHeader: React.FC = () => (
  <p className="text-titleLarge text-neutral-10">
    {CERTIFICATE_LABELS.PREVIEW_TITLE}
  </p>
);

// ─── CreateCertificateTemplate ────────────────────────────────────────────────

/**
 * Create Certificate Template page.
 *
 * Renders in two modes:
 * - **Form mode** — user fills in template name, task form selection, header,
 *   footer, subtitle, checkbox options, and field selection.
 * - **Preview mode** — shows the filled-in name/form selectors (disabled) plus
 *   a `CertificatePreview` with placeholder client/facility data. Save button submits.
 */
const CreateCertificateTemplate: React.FC = () => {
  const {
    formState,
    formOptions,
    formOptionsHasNextPage,
    loadingForms,
    loadingMoreForms,
    loadingFields,
    isPreviewValidating,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setCertTemplateName,
    setFormTemplate,
    handleFormSearch,
    handleFormSearchClear,
    handleFormScrollEnd,
    setHeaderField,
    setFooterField,
    setSubtitle,
    setIncludeTechnicianDetail,
    handleToggleField,
    handleOpenPreview,
    handleClosePreview,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
  } = useCreateCertTemplate();

  // Scroll to first error field on validation attempt
  useEffect(() => {
    if (validationAttemptCount === 0) {
      return;
    }
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-error-field="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [validationAttemptCount]);

  const handlePreviewSubmit = useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleOpenPreview();
    },
    [handleOpenPreview]
  );

  const handleSaveSubmit = useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleSaveFromPreview();
    },
    [handleSaveFromPreview]
  );

  // ─── Preview mode ────────────────────────────────────────────────────────────

  if (isPreviewOpen) {
    const previewTemplate: CertificateTemplateDetail = {
      id: '',
      certTemplateId: '',
      certTemplateName: formState.certTemplateName,
      headerField: formState.headerField,
      footerField: formState.footerField,
      subtitle: formState.subtitle || null,
      includeTechnicianDetail: formState.includeTechnicianDetail,
      formTemplate: null,
      selectedFormFields: formState.availableFormFields
        .filter((f) => formState.selectedFormFieldIds.includes(f.id))
        .map((f) => ({ id: f.id, label: f.label, type: 'text' })),
      createdBy: null,
      updatedBy: null,
      createdAt: '',
      updatedAt: '',
    };

    const formMenuItems = formOptions.map((o) => ({
      id: o.value,
      label: o.label,
    }));
    const selectedFormLabel =
      formOptions.find((o) => o.value === formState.formTemplateId)?.label ??
      '';

    return (
      <CreateLayout
        key="preview"
        isSubmitting={isSubmitting}
        handleSubmit={handleSaveSubmit}
        handleCancel={handleClosePreview}
        isCancelModalOpen={false}
        handleCloseCancelModal={() => {}}
        handleConfirmCancel={() => {}}
        isSnackbarOpen={isErrorSnackbarOpen}
        handleCloseSnackbar={handleCloseErrorSnackbar}
        snackbarError={snackbarError}
        cancelButtonLabel={CERTIFICATE_LABELS.BUTTON_BACK}
        submitButtonLabel={CERTIFICATE_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={CERTIFICATE_LABELS.BUTTON_SAVING}
        Header={PreviewPageHeader}
        headerClassName="mb-5 py-2"
      >
        {/* Disabled fields row — scrolls with the rest of the container (no sticky) */}
        <div className="grid grid-cols-3 gap-4">
          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_TEMPLATE_NAME}
              value={formState.certTemplateName}
              disabled
              fullWidth
            />
          </div>
          <div className="min-w-0">
            <Dropdown
              items={formMenuItems}
              selectedId={formState.formTemplateId}
              disabled
              menuFullWidth
              toolTip={false}
              trigger={({ open, toggle, disabled: dis }) => (
                <DropDownButton
                  label={CERTIFICATE_LABELS.FIELD_FORM_SELECT}
                  value={selectedFormLabel}
                  open={open}
                  toggle={toggle}
                  disabled={dis}
                />
              )}
            />
          </div>
          {formState.formTemplateId && (
            <div className="min-w-0">
              <TextField
                label={CERTIFICATE_LABELS.FIELD_TASK_FORM_ID}
                value={formState.formTemplateDisplayId}
                disabled
                fullWidth
              />
            </div>
          )}
        </div>

        <SectionDivider />

        <CertificatePreview
          template={previewTemplate}
          className="w-full bg-white"
        />
      </CreateLayout>
    );
  }

  // ─── Form mode ───────────────────────────────────────────────────────────────

  const formMenuItems = loadingForms
    ? [{ id: '__loading__', label: 'Ładowanie...', disabled: true as const }]
    : [
        ...formOptions.map((o) => ({
          id: o.value,
          label: `${o.label} (${o.formId})`,
        })),
        ...(loadingMoreForms
          ? [
              {
                id: '__loading_more__',
                label: 'Ładowanie...',
                disabled: true as const,
              },
            ]
          : []),
        ...(!loadingForms && !loadingMoreForms && formOptions.length === 0
          ? [
              {
                id: '__no_results__',
                label: 'Brak wyników',
                disabled: true as const,
              },
            ]
          : []),
      ];

  const selectedFormLabel =
    formOptions.find((o) => o.value === formState.formTemplateId)?.label ?? '';

  return (
    <CreateLayout
      key="edit"
      isSubmitting={isPreviewValidating}
      handleSubmit={handlePreviewSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isErrorSnackbarOpen}
      handleCloseSnackbar={handleCloseErrorSnackbar}
      snackbarError={snackbarError}
      cancelButtonLabel={CERTIFICATE_LABELS.BUTTON_CANCEL}
      submitButtonLabel={CERTIFICATE_LABELS.BUTTON_PREVIEW}
      submitButtonLoadingLabel={CERTIFICATE_LABELS.BUTTON_PREVIEW}
      Header={FormPageHeader}
      headerClassName="mb-5 py-2"
    >
      {/* ── Row 1: Template name + Form selector ── */}
      <div className="grid grid-cols-3 gap-4">
        <div
          className="min-w-0"
          data-error-field={errors.certTemplateName ? 'true' : undefined}
        >
          <TextField
            label={CERTIFICATE_LABELS.FIELD_TEMPLATE_NAME}
            value={formState.certTemplateName}
            onChange={(e) => setCertTemplateName(e.target.value)}
            maxLength={200}
            fullWidth
            autoFocus
            autoComplete="off"
            error={Boolean(errors.certTemplateName)}
            errorMessage={errors.certTemplateName ?? ''}
          />
        </div>

        <div
          className="min-w-0"
          data-error-field={errors.formTemplateId ? 'true' : undefined}
        >
          <Dropdown
            menuFullWidth
            toolTip={false}
            showSearch
            searchPlaceholder="Szukaj formularza..."
            menuMaxVisibleItems={6}
            items={formMenuItems}
            placement="bottom-start"
            error={Boolean(errors.formTemplateId)}
            {...(errors.formTemplateId
              ? { errorMessage: errors.formTemplateId }
              : {})}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                handleFormSearchClear();
              }
            }}
            onItemClick={(id) => {
              if (
                id !== '__loading__' &&
                id !== '__loading_more__' &&
                id !== '__no_results__'
              ) {
                setFormTemplate(id);
              }
            }}
            {...(formOptionsHasNextPage || loadingMoreForms
              ? { onScrollEnd: () => void handleFormScrollEnd() }
              : {})}
            onSearchChange={handleFormSearch}
            trigger={({
              open,
              toggle,
              disabled: dis,
              readOnly,
              error: err,
            }) => (
              <DropDownButton
                label={CERTIFICATE_LABELS.FIELD_FORM_SELECT}
                value={selectedFormLabel}
                open={open}
                toggle={toggle}
                disabled={dis}
                readOnly={readOnly ?? false}
                error={err}
              />
            )}
          />
        </div>

        {/* Task Form ID — appears once a task form is selected */}
        {formState.formTemplateId && (
          <div className="min-w-0">
            <TextField
              label={CERTIFICATE_LABELS.FIELD_TASK_FORM_ID}
              value={formState.formTemplateDisplayId}
              disabled
              fullWidth
            />
          </div>
        )}
      </div>

      <SectionDivider />

      {/* ── Certificate data section ── */}
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-[18px] font-normal leading-6.5 text-[#474A4C]">
            {CERTIFICATE_LABELS.SECTION_CERTIFICATE_DATA}
          </h2>
          <p className="text-sm leading-5 tracking-[0.25px] text-[#474A4C] mt-1">
            {CERTIFICATE_LABELS.SECTION_CERTIFICATE_DATA_DESC}
          </p>
        </div>

        {/* Header + Footer row */}
        <div className="grid grid-cols-3 gap-4">
          <div
            className="min-w-0"
            data-error-field={errors.headerField ? 'true' : undefined}
          >
            <TextField
              label={CERTIFICATE_LABELS.FIELD_HEADER}
              value={formState.headerField}
              onChange={(e) => setHeaderField(e.target.value)}
              maxLength={200}
              fullWidth
              error={Boolean(errors.headerField)}
              errorMessage={errors.headerField ?? ''}
            />
          </div>

          <div
            className="min-w-0"
            data-error-field={errors.footerField ? 'true' : undefined}
          >
            <TextField
              label={CERTIFICATE_LABELS.FIELD_FOOTER}
              value={formState.footerField}
              onChange={(e) => setFooterField(e.target.value)}
              maxLength={200}
              fullWidth
              error={Boolean(errors.footerField)}
              errorMessage={errors.footerField ?? ''}
            />
          </div>
        </div>

        {/* Subtitle textarea */}
        <div data-error-field={errors.subtitle ? 'true' : undefined}>
          <Textarea
            label={CERTIFICATE_LABELS.FIELD_SUBTITLE}
            value={formState.subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            maxLength={500}
            fullWidth
            rows={3}
            error={Boolean(errors.subtitle)}
            errorMessage={errors.subtitle ?? ''}
          />
        </div>
      </div>

      {/* ── Auto-data checkboxes (sibling section, gap-8 from cert data via CreateLayout) ── */}
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-5 tracking-[0.25px] text-[#474A4C]">
          {CERTIFICATE_LABELS.AUTO_DATA_SECTION}
        </p>
        <div className="flex flex-col gap-1">
          <Checkbox
            checked
            disabled
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_CLIENT_FACILITY}
          />
          <Checkbox
            checked
            disabled
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_SERVICE_REQUEST}
          />
          <Checkbox
            checked={formState.includeTechnicianDetail}
            onChange={(checked) => setIncludeTechnicianDetail(checked)}
            labelClassName="font-normal"
            label={CERTIFICATE_LABELS.CHECK_TECHNICIAN}
          />
        </div>
      </div>

      {/* ── Form fields section — always visible ── */}
      <SectionDivider />

      <div className="flex flex-col gap-4">
        <div
          data-error-field={errors.selectedFormFieldIds ? 'true' : undefined}
        >
          <p className="text-sm font-normal leading-5 tracking-[0.25px] text-[#474A4C] wrap-break-word">
            {CERTIFICATE_LABELS.SECTION_FORM_FIELDS_DESC}
          </p>
          {errors.selectedFormFieldIds && (
            <p className="text-xs text-[#e11b1b] mt-1">
              {errors.selectedFormFieldIds}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {/* Dynamic fields from selected task form */}
          {!formState.formTemplateId ? (
            <p className="text-sm text-[#9B9EA0]">
              {CERTIFICATE_LABELS.FIELD_SELECTION_PLACEHOLDER}
            </p>
          ) : loadingFields ? (
            <>
              <FieldRowShimmer />
              <FieldRowShimmer />
              <FieldRowShimmer />
            </>
          ) : formState.availableFormFields.length === 0 ? (
            <p className="text-sm text-[#9B9EA0]">
              {CERTIFICATE_LABELS.FIELD_SELECTION_NO_FIELDS}
            </p>
          ) : (
            <>
              {formState.availableFormFields.map((field) => (
                <div
                  key={field.id}
                  className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5 cursor-pointer has-focus-visible:ring-2 has-focus-visible:ring-[#181A1B] has-focus-visible:ring-offset-1 [&_label:has(:focus-visible)]:[box-shadow:none]!"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      handleToggleField(field.id);
                    }
                  }}
                >
                  <Checkbox
                    checked={formState.selectedFormFieldIds.includes(field.id)}
                    onChange={() => handleToggleField(field.id)}
                    labelClassName="font-normal"
                    label={field.label}
                  />
                </div>
              ))}
            </>
          )}

          {/* Mandatory fields — always present, cannot be deselected */}
          <div className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5">
            <Checkbox
              checked
              disabled
              labelClassName="font-normal"
              label={CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_SIGNATURE}
            />
          </div>
          <div className="border border-[#DCDDE0] rounded-lg overflow-clip px-4 py-2.5">
            <Checkbox
              checked
              disabled
              labelClassName="font-normal"
              label={CERTIFICATE_PREVIEW_LABELS.CLIENT_SIGNATURE}
            />
          </div>
        </div>
      </div>
    </CreateLayout>
  );
};

export default CreateCertificateTemplate;
